/**
 * Site-copy CMS API module. One function per documented endpoint in
 * `plustown-be/docs/api/site-copy/admin-*.md`.
 *
 * Reads go through `request(…, schema)`; writes parse their body with the
 * request schema first and wrap failures with `toApiError` so the query layer
 * can branch on `code` (notably `version_conflict`).
 */

import { api, request } from '../client';
import { ApiError, toApiError } from '../errors';
import {
  copyBundleDetailSchema,
  copyBundleListResponseSchema,
  copyBundleRevertRequestSchema,
  copyBundleRevertResponseSchema,
  copyCatalogResponseSchema,
  copyClearResponseSchema,
  copyDraftSaveResponseSchema,
  copyDraftUpsertSchema,
  copyKeyDetailSchema,
  copyPendingResponseSchema,
  copyPublishRequestSchema,
  copyPublishResponseSchema,
  copySyncReportSchema,
  copySyncRequestSchema,
  type CopyBundleDetail,
  type CopyBundleListResponse,
  type CopyBundleRevertRequest,
  type CopyBundleRevertResponse,
  type CopyCatalogFilters,
  type CopyCatalogResponse,
  type CopyClearResponse,
  type CopyDraftSaveResponse,
  type CopyDraftUpsert,
  type CopyKeyDetail,
  type CopyLocale,
  type CopyPendingResponse,
  type CopyPublishRequest,
  type CopyPublishResponse,
  type CopySyncReport,
  type CopySyncRequest,
} from '@/schemas/admin/copy';

const BASE = 'admin/v1/site-copy';

/** `version_conflict` is the one write failure with a bespoke recovery path. */
export function isCopyConflict(err: unknown): err is ApiError {
  return err instanceof ApiError && (err.status === 409 || err.code === 'version_conflict');
}

function searchParamsOf(filters: CopyCatalogFilters): Record<string, string> {
  const out: Record<string, string> = {};
  if (filters.q) out.q = filters.q;
  if (filters.namespace) out.namespace = filters.namespace;
  if (filters.surface) out.surface = filters.surface;
  if (filters.status) out.status = filters.status;
  if (filters.flag) out.flag = filters.flag;
  if (filters.limit) out.limit = String(filters.limit);
  if (filters.cursor) out.cursor = filters.cursor;
  return out;
}

/** Copy keys are dot paths; the backend re-validates after `url.PathUnescape`. */
function encodeKey(key: string): string {
  return encodeURIComponent(key);
}

export function listCopyCatalog(
  filters: CopyCatalogFilters,
  signal?: AbortSignal
): Promise<CopyCatalogResponse> {
  const searchParams = searchParamsOf(filters);
  const opts = signal ? { signal, searchParams } : { searchParams };
  return request(() => api.get(`${BASE}/catalog`, opts).json<unknown>(), copyCatalogResponseSchema);
}

export function getCopyKey(key: string, signal?: AbortSignal): Promise<CopyKeyDetail> {
  return request(
    () => api.get(`${BASE}/keys/${encodeKey(key)}`, signal ? { signal } : {}).json<unknown>(),
    copyKeyDetailSchema
  );
}

export async function saveCopyDraft(
  key: string,
  locale: CopyLocale,
  input: CopyDraftUpsert
): Promise<CopyDraftSaveResponse> {
  const body = copyDraftUpsertSchema.parse(input);
  try {
    const raw = await api
      .put(`${BASE}/keys/${encodeKey(key)}/locales/${locale}`, { json: body })
      .json<unknown>();
    return copyDraftSaveResponseSchema.parse(raw);
  } catch (e) {
    throw await toApiError(e);
  }
}

export async function clearCopyOverride(
  key: string,
  locale: CopyLocale
): Promise<CopyClearResponse> {
  try {
    const raw = await api
      .delete(`${BASE}/keys/${encodeKey(key)}/locales/${locale}`)
      .json<unknown>();
    return copyClearResponseSchema.parse(raw);
  } catch (e) {
    throw await toApiError(e);
  }
}

export function getCopyPending(signal?: AbortSignal): Promise<CopyPendingResponse> {
  return request(
    () => api.get(`${BASE}/pending`, signal ? { signal } : {}).json<unknown>(),
    copyPendingResponseSchema
  );
}

export async function publishCopy(input: CopyPublishRequest): Promise<CopyPublishResponse> {
  const body = copyPublishRequestSchema.parse(input);
  try {
    const raw = await api.post(`${BASE}/publish`, { json: body }).json<unknown>();
    return copyPublishResponseSchema.parse(raw);
  } catch (e) {
    throw await toApiError(e);
  }
}

/**
 * Reconcile the catalog with the live site's `copy-manifest.json`. The API
 * resolves the manifest from its own `COPY_SITE_BASE_URL`; the fetch alone may
 * take up to 10 s server-side, so the client allows more than its 20 s default.
 */
export async function syncCopyCatalog(input: CopySyncRequest = {}): Promise<CopySyncReport> {
  const body = copySyncRequestSchema.parse(input);
  try {
    const raw = await api.post(`${BASE}/sync`, { json: body, timeout: 30_000 }).json<unknown>();
    return copySyncReportSchema.parse(raw);
  } catch (e) {
    throw await toApiError(e);
  }
}

export function listCopyBundles(
  params: { limit?: number; cursor?: string },
  signal?: AbortSignal
): Promise<CopyBundleListResponse> {
  const searchParams: Record<string, string> = {};
  if (params.limit) searchParams.limit = String(params.limit);
  if (params.cursor) searchParams.cursor = params.cursor;
  const opts = signal ? { signal, searchParams } : { searchParams };
  return request(
    () => api.get(`${BASE}/bundles`, opts).json<unknown>(),
    copyBundleListResponseSchema
  );
}

export function getCopyBundle(version: number, signal?: AbortSignal): Promise<CopyBundleDetail> {
  return request(
    () => api.get(`${BASE}/bundles/${version}`, signal ? { signal } : {}).json<unknown>(),
    copyBundleDetailSchema
  );
}

export async function revertCopyBundle(
  version: number,
  input: CopyBundleRevertRequest = {}
): Promise<CopyBundleRevertResponse> {
  const body = copyBundleRevertRequestSchema.parse(input);
  try {
    const raw = await api.post(`${BASE}/bundles/${version}/revert`, { json: body }).json<unknown>();
    return copyBundleRevertResponseSchema.parse(raw);
  } catch (e) {
    throw await toApiError(e);
  }
}
